import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import type {
    ILicenseInheritance,
    IPersonaFit,
    IUserDecoratedLicense,
    Token,
} from '@experiences/interfaces';
import {
    DEFAULT_PERSONA_FIT,
    ERROR_ON_LICENSE_API,
    ERROR_ON_USERS_API,
    getPersonaFit,
} from '@experiences/util';
import { jwtDecode } from 'jwt-decode';
import union from 'lodash/union';

import {
    get,
    post,
} from '../utility/Requests';

export const personaUri = '/api/persona/persona/';

// Onprem keeps using the Express proxy because the cluster ingress in this repo
// does not expose `/lease_/` to the browser. Cloud calls LA directly.
const isOnPremEnv = () => Boolean(window.env?.isOnPrem) || window.env?.name === 'k8s';

export async function getPersona(token: string): Promise<IPersonaFit> {
    const profile = jwtDecode<Token>(token);
    const userId = profile.sub;

    if (isOnPremEnv()) {
        return get<IPersonaFit>(personaUri + userId, { accessToken: token });
    }

    return getPersonaDirect(token, profile);
}

async function getPersonaDirect(token: string, profile: Token): Promise<IPersonaFit> {
    const userId = profile.sub;
    const organizationId = profile.prt_id;

    const isHostMode = window.env?.enableHostMode && profile.host?.toLowerCase() === 'true';
    if (isHostMode) {
        return { fit: DEFAULT_PERSONA_FIT };
    }

    const baseUrl = `/lease_/api/account/${organizationId}/user-license`;
    const directCallProps = {
        accessToken: token,
        stripPortalPath: true,
        headers: { extendRequestHeaders: 'true' },
    };

    let licenseData: IUserDecoratedLicense[];
    try {
        licenseData = await post<IUserDecoratedLicense[]>(`${baseUrl}/license/users`, {
            ...directCallProps,
            body: { userIds: [ userId ] },
        });
    } catch {
        return {
            fit: DEFAULT_PERSONA_FIT,
            errorMessage: ERROR_ON_USERS_API(userId),
        };
    }

    const enableLicenseInheritance = getFeatureFlagValue(Features.EnableLicenseInheritance.name);

    let licenseInheritance: ILicenseInheritance | undefined;
    if (enableLicenseInheritance) {
        try {
            licenseInheritance = await get<ILicenseInheritance>(
                `${baseUrl}/licenseInheritance/${userId}`,
                directCallProps,
            );
        } catch {
            return {
                fit: DEFAULT_PERSONA_FIT,
                errorMessage: ERROR_ON_LICENSE_API(userId),
            };
        }
    }

    const currentlyUsedLicenses = licenseInheritance
        ? licenseInheritance.currentlyUsedLicense.map(l => l.userBundleCodes)
        : [];
    const inheritedLicenses = licenseInheritance
        ? licenseInheritance.allGroupLicenseAllocationRules.map(l => l.userBundleCodes)
        : [];

    const primaryLicense: string[] = union(...currentlyUsedLicenses, licenseData[0].userBundleCodes);
    const secondaryLicenses: string[] = union(...inheritedLicenses);

    return getPersonaFit(primaryLicense, secondaryLicenses);
}
